import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppLayout } from '@/components/AppLayout/AppLayoutProvider'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import { useRunnerState } from '@/composables/useRunnerState'
import useTracking from '@/composables/useTracking'
import { CompletionStatus } from '@/state/AppStateMachine'

import Button from '../Button/Button'
import { useStatsigClient } from '../StatsigProvider/StatsigProvider'

export default function RunnerTooltip() {
  const { gsap } = useGSAP()
  const { gameState } = useRunnerState()
  const { trackImpression, trackClick } = useTracking()
  const { scenario, persona, scenarioUnitName, context } = useAppState()
  const statsigClient = useStatsigClient()
  const { t } = useTranslation()
  const { root } = useAppLayout()
  const ref = useRef<HTMLDivElement>(null)
  const [listOfTooltips, setListOfTooltips] = useState<string[]>([])
  const isFirstScenario = scenario.status.some((status) => status === CompletionStatus.NOT_STARTED)
  const [anchorPos, setAnchorPos] = useState<{ top: number; left: number } | null>(null)

  useEffect(() => {
    if (gameState.tooltip) {
      setListOfTooltips((prev) => [...prev, gameState.tooltip])
    }
  }, [gameState.tooltip])

  const nextTooltip = listOfTooltips[0]
  const text = t('tooltips.' + nextTooltip + '.text', '')
  const textDesktop = t('tooltips.' + nextTooltip + '_desktop.text', '')
  const playOnce = t('tooltips.' + nextTooltip + '.play_once', 'false') === 'true'
  const element = t('tooltips.' + nextTooltip + '.element', '')
  const link = t('tooltips.' + nextTooltip + '.link', '')
  const cta = t('tooltips.' + nextTooltip + '.cta', 'Okay')
  const duration = Number(t('tooltips.' + nextTooltip + '.duration', '7000'))

  const onDismiss = () => {
    if (!ref.current) {
      setListOfTooltips((prev) => prev.slice(1))
      return
    }
    // animate it out
    gsap.to(ref.current, {
      opacity: 0,
      scale: 0,
      y: 20,
      duration: 0.35,
      ease: 'power2.inOut',
      onComplete: () => {
        setListOfTooltips((prev) => prev.slice(1))
      },
    })
  }

  const handleOkayClick = () => {
    trackClick({
      clickType: 'button',
      unitName: `${persona}_simulation${scenarioUnitName}`,
      unitVariation: `tooltip_${nextTooltip}`,
      unitLocation: 'tyce',
      buttonText: link ? cta.toLowerCase() : 'okay',
    })

    onDismiss()
  }

  useEffect(() => {
    if (!nextTooltip) return

    trackImpression({
      unitName: `${persona}_simulation${scenarioUnitName}`,
      unitVariation: `tooltip_${nextTooltip}`,
      unitLocation: 'tyce',
    })
  }, [nextTooltip])

  // animate in when a new tooltip becomes active
  useEffect(() => {
    if (!ref.current || !nextTooltip) return
    gsap.fromTo(
      ref.current,
      { opacity: 0, scale: 0, y: -100 },
      { opacity: 1, scale: 1, y: 0, duration: 0.35, ease: 'power2.out' }
    )
  }, [gsap, nextTooltip])

  useEffect(() => {
    if (!listOfTooltips.length) return
    // auto-hide after the tooltip's duration (defaults to 7s)
    const setTimeoutRef = window.setTimeout(() => onDismiss(), duration)
    return () => window.clearTimeout(setTimeoutRef)
  }, [listOfTooltips, duration])

  useEffect(() => {
    const anchorEl = element ? (root.current.querySelector(element) as HTMLElement | null) : null
    if (!anchorEl) {
      setAnchorPos(null)
      return
    }
    const rect = anchorEl.getBoundingClientRect()
    const gap = 12
    const top = rect.top - gap
    const left = rect.left + rect.width + gap

    setAnchorPos({ top: Math.max(8, top), left })
  }, [element, nextTooltip])

  // hide the morning-routine "watch" tooltip for users who selected "a kid!"
  const hideForChild = nextTooltip === 'watch' && context.relationship === 'CHILD'

  if (!text || (playOnce && !isFirstScenario) || hideForChild) return null

  const anchored = !!element && !!anchorPos

  return (
    <div
      className={`absolute max-w-max w-[90%] ${anchored ? '-translate-y-[100%] lg:-translate-[100%]' : 'left-1/2 bottom-[90px] lg:bottom-auto lg:top-[60%] -translate-x-1/2'}`}
      style={anchored ? ({ top: anchorPos!.top, left: anchorPos!.left } as React.CSSProperties) : undefined}
      role='status'
    >
      <div ref={ref} className='flex items-center pr-12 rounded-12 bg-dark-blue text-cream text-16 md:text-18'>
        <div className='px-12 py-20 font-semibold md:whitespace-nowrap'>
          <span className='lg:hidden'>{text}</span>
          <span className='hidden lg:inline'>{textDesktop || text}</span>
        </div>
        <div>
          <Button
            animate={false}
            size='small'
            variant='tertiary'
            className='pointer-events-auto text-dark-blue'
            is={link ? 'a' : 'button'}
            href={link || undefined}
            target={link ? '_blank' : undefined}
            onClick={() => handleOkayClick()}
          >
            {cta}
          </Button>
        </div>

        {!!element && (
          <span
            aria-hidden='true'
            className='pointer-events-none absolute right-[calc(50%-10px)] lg:right-[25px] -bottom-[9px] w-0 h-0 border-l-[10px] border-r-[10px] border-t-[10px] border-l-transparent border-r-transparent border-t-dark-blue'
          />
        )}
      </div>
    </div>
  )
}
